
import { useState, useEffect } from 'react';
import ApparelService from '@/services/apparelService';
import { ApparelProduct } from '@/types/apparelTypes';
import { Family, Student } from '@/types/models';
import { getCurrentOrgId } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { mapToBillingStatus } from '@/utils/typeUtils';

export function useApparelData(isParentPortal = false) {
  const [products, setProducts] = useState<ApparelProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const orgId = await getCurrentOrgId();
        
        if (!orgId) {
          console.error("No organization ID found");
          setError("Unable to determine organization");
          setLoading(false);
          return;
        }

        // Fetch products
        const productsData = await ApparelService.getProducts(orgId);
        setProducts(productsData);

        // Extract categories
        const uniqueCategories = [...new Set(productsData.map(p => p.category || 'Uncategorized'))];
        setCategories(uniqueCategories.sort());

        // Only fetch families and students if not in parent portal
        // In parent portal mode, these will be provided via context or props
        if (!isParentPortal) {
          // Fetch families
          const { data: familiesData, error: familiesError } = await supabase
            .from('families')
            .select('*')
            .eq('organization_id', orgId)
            .order('name');

          if (familiesError) {
            console.error("Error fetching families:", familiesError);
          } else if (familiesData) {
            // Map billing_status to the correct BillingStatus type
            const typedFamilies = familiesData.map(family => ({
              id: family.id,
              organization_id: family.organization_id,
              name: family.name,
              billing_status: mapToBillingStatus(family.billing_status as string),
              created_at: family.created_at
            })) as Family[];
            
            setFamilies(typedFamilies);
          }

          // Fetch students
          const { data: studentsData, error: studentsError } = await supabase
            .from('students')
            .select('*')
            .eq('organization_id', orgId)
            .order('last_name');

          if (studentsError) {
            console.error("Error fetching students:", studentsError);
          } else {
            const typedStudents = (studentsData || []).map(student => ({
              id: student.id,
              family_id: student.family_id,
              organization_id: student.organization_id,
              first_name: student.first_name,
              last_name: student.last_name,
              date_of_birth: student.date_of_birth,
              gender: student.gender,
              notes: student.notes,
              created_at: student.created_at
            })) as Student[];
            
            setStudents(typedStudents);
          }
        }
      } catch (err) {
        console.error("Error fetching apparel data:", err);
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isParentPortal]);

  const refreshProducts = async () => {
    try {
      setLoading(true);
      const orgId = await getCurrentOrgId();
      if (!orgId) {
        throw new Error("No organization ID found");
      }
      
      const productsData = await ApparelService.getProducts(orgId);
      setProducts(productsData);
      
      const uniqueCategories = [...new Set(productsData.map(p => p.category || 'Uncategorized'))];
      setCategories(uniqueCategories.sort());
    } catch (err) {
      console.error("Error refreshing products:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  return {
    products,
    categories,
    families,
    students,
    loading,
    error,
    refreshProducts
  };
}
